/**
 * Page Templates Library API
 *
 * Allows users to save an entire page (all its blocks) as a reusable
 * template. Templates are global (cross-site) and stored in KV via
 * the server.
 *
 * Each page may have blocks across multiple zones (in-hero, after-hero,
 * service-b-after-advantages, etc.). The template preserves zone info
 * as `_zoneId` metadata on each block, allowing accurate restoration.
 */

import { apiFetch } from "./apiClient";

export interface PageTemplate {
  id: string;
  name: string;
  description: string;
  /** Full array of blocks that make up the page (flattened across zones) */
  blocks: PageTemplateBlock[];
  blockCount: number;
  /** Summary of block types for display, e.g. "heading, text, image" */
  blockSummary: string;
  /** Zone metadata for display */
  zones?: { zoneId: string; blockCount: number }[];
  createdAt: number;
  updatedAt: number;
  createdBy?: string;
}

/** Minimal block shape stored in a page template */
export interface PageTemplateBlock {
  type: string;
  content: string;
  variant?: string;
  meta?: Record<string, any>;
  /** Zone this block belongs to (preserved for zone-aware loading) */
  _zoneId?: string;
}

/** List all page templates (newest first). */
export async function listPageTemplates(): Promise<PageTemplate[]> {
  const data = await apiFetch("/page-templates");
  return data.templates || [];
}

/** Save a new page template. */
export async function savePageTemplate(
  template: Omit<PageTemplate, "id" | "createdAt" | "updatedAt">,
): Promise<PageTemplate> {
  const id = `pt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const now = Date.now();
  const payload: PageTemplate = { ...template, id, createdAt: now, updatedAt: now };
  await apiFetch("/page-templates", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return payload;
}

/** Update a page template's name/description. */
export async function updatePageTemplate(
  id: string,
  updates: Partial<Pick<PageTemplate, "name" | "description">>,
): Promise<void> {
  await apiFetch(`/page-templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}

/** Delete a page template. */
export async function deletePageTemplate(id: string): Promise<void> {
  await apiFetch(`/page-templates/${id}`, { method: "DELETE" });
}
